import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { ICellEditorAngularComp, ICellRendererAngularComp } from 'ag-grid-angular';
import { CurrencyPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { selectCostBasis, selectMultipleToAccountsPositionLotTableData, selectToAccountsInfo } from '../../../create-request/reducers';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { InternalTransfersActions } from '../../../create-request/actions';
import { positionConstants } from '../../utilities/Constants';
import { LotDetails } from '../../../create-request/models/internal-transfer/LotDetails';
import { RowDataById } from '../../../create-request/models/internal-transfer/InternalTransfersInfo';

@Component({
  standalone: false,
    selector: 'mm-lot-text-input-editor-multiple-to-accounts',
    templateUrl: './table-text-lot-input-field-multiple-to-account.component.html',
    styleUrls: ['./table-text-lot-input-field-multiple-to-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class TextFieldMultipleLotComponent implements ICellEditorAngularComp, ICellRendererAngularComp {
    constructor(private store: Store, private currencyPipe: CurrencyPipe, private changeDetection: ChangeDetectorRef) { }
    @ViewChild('inputShares') textInput: any;
    @ViewChild('tooltip') tooltip: any;
    params: any;
    toolTipErrorArray: string[] = []; // array of errors to convert to tooltipError message
    toolTipError: string = ''; // actual error message to display in tool tip
    toolTipErrorCount: number = 0; // running count of total number of errors to be displayed in tooltip error
    currentTextInput: string = '';
    showIcon: boolean = false;
    isInputField: boolean = true;
    showTextbox: boolean = true;
    destroy$: Subject<boolean> = new Subject<boolean>();
    costBasisMethod: string | undefined;
    toolTipErrorQuantiyMessage: null | string | undefined = '';
    enteredLotValue: string = '';
    private lastRenderSyncKey: string | null = null;

    ngOnInit(): void {
        this.store.select(selectCostBasis).pipe(takeUntil(this.destroy$)).subscribe(info => this.costBasisMethod = info);
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    agInit(params: any): void {
        this.handleEnteredLotData(params);
    }

    // In render mode we update the visible error state immediately, then defer the heavier grid/store sync to avoid duplicate AG Grid work.
    handleEnteredLotData(params: any) {
        this.params = params;
        this.enteredLotValue = this.params.value;
        if (this.params.value !== undefined) {
            this.updateErrors();
            if (params.mode !== 'render') {
                this.lastRenderSyncKey = null;
            }
            this.changeDetection.detectChanges();

            setTimeout(() => {
                if (params.mode === 'render') {
                    if (this.shouldRunRenderSync()) {
                        this.handleTableCalculations();
                        this.updateStorePositions();
                        this.handlePositionRowTableCalculations();
                    }
                }
                this.changeDetection.detectChanges();
            }, 0);
        }
    }

    refresh(params: any): boolean {
        this.handleEnteredLotData(params);
        return true;
    }

    //This function returns the value currently in the textbox - called by the grid once after editing is complete
    getValue() {
        return this.textInput?.nativeElement?.value ?? this.enteredLotValue;
    }

    // Skip the expensive render sync when AG Grid rerenders the same visible lot state more than once.
    private shouldRunRenderSync(): boolean {
        const nextRenderSyncKey = this.getRenderSyncKey();
        if (nextRenderSyncKey === this.lastRenderSyncKey) {
            return false;
        }

        this.lastRenderSyncKey = nextRenderSyncKey;
        return true;
    }

    private getRenderSyncKey(): string {
        return [
            this.params?.node?.id ?? this.params?.node?.rowIndex ?? '',
            this.params?.column?.getColId?.() ?? '',
            this.enteredLotValue ?? '',
            this.CurrentAvailableLots,
            this.params?.data?.[this.EmvColumn] ?? '',
            this.params?.data?.[this.TotalCostBasisColumn] ?? '',
            this.params?.data?.['lotHasError ' + this.AccountId] ?? ''
        ].join('|');
    }

    isCancelAfterEnd(): boolean {
        this.onchange();
        return false;
    }

    onchange(shouldRefresh: boolean = true): boolean {
        if (!this.isManualFlow) {
            return false;
        }

        const normalizedValue = this.getNormalizedInputValue();
        const currentTableValue = this.TableLotsToMove?.toString() ?? '';
        if (normalizedValue === this.enteredLotValue && currentTableValue === normalizedValue) {
            return false;
        }

        this.enteredLotValue = normalizedValue;
        this.params.value = normalizedValue;
        if (this.textInput?.nativeElement) {
            this.textInput.nativeElement.value = normalizedValue;
        }

        this.updateLotsToMove(normalizedValue);
        this.updateErrors();
        this.handleTableCalculations();
        this.handlePositionRowTableCalculations();
        this.updateStorePositions();
        this.changeDetection.detectChanges();
        if (shouldRefresh) {
            this.scheduleRowRefresh();
        }

        return true;
    }

    onBlur() {
        const didCommit = this.onchange(false);
        setTimeout(() => {
            this.params?.stopEditing?.();
            if (didCommit) {
                this.scheduleRowRefresh();
            }
        }, 0);
    }

    onkeydown(event: KeyboardEvent) {
        if (event.code === 'Enter') {
            const didCommit = this.onchange(false);
            event.preventDefault();
            event.stopPropagation();
            setTimeout(() => {
                this.params?.stopEditing?.();
                if (didCommit) {
                    this.scheduleRowRefresh();
                }
            }, 0);
        } else if (event.code === 'Tab') {
            this.onchange();
        } else if (event.code === 'Backspace') {
            const inputElement = this.textInput?.nativeElement;
            const inputString = inputElement?.value?.toString() ?? '';
            const isFullSelection = !!inputElement
                && inputString.length > 0
                && inputElement.selectionStart === 0
                && inputElement.selectionEnd === inputString.length;

            if (isFullSelection) {
                event.stopPropagation();
                inputElement.value = '';
                this.onchange();
            }
        }
    }

    private getNormalizedInputValue(): string {
        const currentInputValue = this.textInput?.nativeElement?.value;
        return isNaN(currentInputValue) || Number(currentInputValue) === 0 ? '' : Number(currentInputValue).toString();
    }

    private scheduleRowRefresh(): void {
        setTimeout(() => {
            this.params?.api?.refreshCells({ rowNodes: [this.params.node], force: true });
        }, 0);
    }

    // Keep all lot-level validation output in one place so the tooltip, icon, and row height stay synchronized.
    updateErrors(): void {
        let numberofErrors = 0;
        let multipleLineErrorCount = 0;
        let hasError = false;
        const nextToolTipErrorArray: string[] = [];
        if (this.enteredLotValue !== '') {
            if (this.hasMoreThanSixDecimals(this.enteredLotValue)) {
                this.addTooltipError(positionConstants.lotExceedsSixDecimalsWarningMessage, nextToolTipErrorArray);
                numberofErrors++;
                multipleLineErrorCount++;
                this.params.node.setDataValue('lotHasError ' + this.AccountId, numberofErrors);
                this.params.node.setDataValue('lotMultipleLineErrorCount ' + this.AccountId, multipleLineErrorCount);
                hasError = true;
            }
            if (this.hasExceedsAvailableSharesError()) {
                this.addTooltipError(positionConstants.positionQuantityExceedsAvailableShares, nextToolTipErrorArray);
                numberofErrors++;
                this.params.node.setDataValue('lotHasError ' + this.AccountId, numberofErrors);
                this.params.node.setDataValue('lotMultipleLineErrorCount ' + this.AccountId, multipleLineErrorCount);
                hasError = true;
            }
        }
        this.showIcon = hasError;
        this.toolTipErrorCount = numberofErrors;
        this.toolTipErrorArray = nextToolTipErrorArray;
        this.toolTipError = nextToolTipErrorArray.join("\n");
        this.toolTipErrorQuantiyMessage = numberofErrors === 0 ? '' : numberofErrors === 1 ? '1 error' : `${numberofErrors} errors`;
        if (!hasError) {
            this.params.node.setDataValue('lotHasError ' + this.AccountId, 0);
            this.params.node.setDataValue('lotMultipleLineErrorCount ' + this.AccountId, 0);
        }

        this.store.select(selectToAccountsInfo).pipe(take(1)).subscribe(toAccountInfo => {
            let errorCount = 0;
            let numberOfMultipleLineErrors = 0;
            for (const key in toAccountInfo) {
                if (this.params.node.data['lotHasError ' + key] > 0) {
                    if (this.params.node.data['lotHasError ' + key] > errorCount) {
                        errorCount = this.params.node.data['lotHasError ' + key];
                    }
                }
                if (this.params.node.data['lotMultipleLineErrorCount ' + key] > 0) {
                    if (this.params.node.data['lotMultipleLineErrorCount ' + key] > numberOfMultipleLineErrors) {
                        numberOfMultipleLineErrors = this.params.node.data['lotMultipleLineErrorCount ' + key];
                    }
                }
            }
            if (this.enteredLotValue !== '') {
                this.setRowHeight(55);
            }
            if (errorCount <= 0) {
                this.setRowHeight(positionConstants.tableRowHeight);
            }
        });
    }

    hasError(value: string | number): boolean {
        if (value !== '') {
            if (this.hasMoreThanSixDecimals(value.toString()) || this.hasExceedsAvailableSharesError()) {
                return true;
            }
        }
        return false;
    }

    hasMoreThanSixDecimals(lotsToMove: string): boolean {
        if (lotsToMove?.split('.')[1]?.length > 6) {
            return true;
        }
        return false;;
    }

    hasExceedsAvailableSharesError(): boolean {
        return this.CurrentAvailableLots < 0;
    }

    isEmptyOrNull(param: any) {
        if (param == undefined || param == null || param == '') {
            return true
        }
        return false
    }

    addTooltipError(errorMessage: string, toolTipErrorArray: string[]): void {
        const nextToolTipErrorCount = toolTipErrorArray.length + 1;
        if (nextToolTipErrorCount === 1) {
            // don't show number when only one error in array
            this.setTooltipErrorMessage(errorMessage, toolTipErrorArray);
        } else if (toolTipErrorArray[0].charAt(0) !== '1') {
            // add number 1 to first error in array
            const error = '1. '.concat(toolTipErrorArray[0]);
            toolTipErrorArray[0] = error;
            // add second error to array
            this.setTooltipErrorMessage(`${nextToolTipErrorCount}. ${errorMessage}`, toolTipErrorArray);
        } else {
            // add 3+ error to array
            this.setTooltipErrorMessage(`${nextToolTipErrorCount}. ${errorMessage}`, toolTipErrorArray);
        }
    }

    setTooltipErrorMessage(messageToAdd: string, toolTipErrorArray: string[]): void {
        toolTipErrorArray.push(messageToAdd);
    }

    setRowHeight(rowHeight: number): void {
        this.params?.node?.setRowHeight(rowHeight);
        setTimeout(() => {
            this.params?.api?.onRowHeightChanged();
        }, 0);
    }

    afterGuiAttached() {
        this.textInput.nativeElement.focus();
        this.textInput.nativeElement.select();
    }

    get AccountId() {
        var columnFieldSplit = this.params.column.getColId().split(' ');
        return columnFieldSplit[columnFieldSplit.length - 1];
    }

    get TableLotsToMove() {
        return this.params.node.data['Lots to move ' + this.AccountId]
    }

    get TableSharesToMove() {
        return this.params.node.data['Shares to move ' + this.AccountId]
    }

    get LotsCostBasis() {
        return Number(this.params.node.data['Cost Basis'].replaceAll('$', '').replaceAll(',', ''))
    }

    calculateTotalCostBasis(sharestoMove: number, costBasis: number, totalShares: number) {
        return (costBasis / totalShares) * sharestoMove * Math.sign(costBasis);
    }

    get PricePerShare() {
        return Number(this.params.node.data['Current price/share'].replace('$', '').replaceAll(',',''))
    }

    get ValMultiplier() {
        return Number(this.params.node.data['valMultiplier'])
    }

    calculateEmv(sharesToMove: number) {
        return (this.PricePerShare * sharesToMove * this.ValMultiplier) * Math.sign(this.TotalAvailableSharesLotLevel);
    }

    get EmvColumn() {
        return 'Estimated market value ' + this.AccountId;
    }

    get TotalCostBasisColumn() {
        return 'TotalCostBasis ' + this.AccountId;
    }
    
    get LotsToMoveColumn() {
        return 'Lots to move ' + this.AccountId;
    }

    get MasterTotalCostBasisColumn() {
        return 'Total cost basis ' + this.AccountId;
    }

    get isManualFlow() {
        return this.costBasisMethod === 'None'
    }

    get CurrentAvailableLots() {
        return Object.keys(this.params.node.data).filter(properties => properties.startsWith('Lots to move')).reduce((availableShares, enteredShare) => 
            availableShares - Number(this.params.node.data[enteredShare]), Math.abs(this.TotalAvailableSharesLotLevel));
    }

    getMasterCurentAvailableShares() {
        return Number(this.params.data.masterNode.node.data['currentAvailableShares'])
    }

    get TotalAvailableSharesLotLevel() {
        return Number(this.params.node.data['Holding Period'].split(' ')[0].replaceAll(',', ''))
    }

    updateEmv(emv: number | string) {
        this.params.node?.setDataValue(this.EmvColumn, this.currencyPipe.transform(emv))
    }

    updateLotsToMove(lotsToMove: number | string) {
        this.params.node?.setDataValue(this.LotsToMoveColumn, lotsToMove);
    }

    updateTotalCostBasis(totalCostBasis: number | string, accountId: string) {
        this.params.node?.setDataValue('TotalCostBasis ' + accountId, this.currencyPipe.transform(totalCostBasis))
    }

    updateMasterSharesToMove(sharesToMove: number | string, accountId: string) {
        this.params.data.masterNode.node?.setDataValue('Shares to move ' + accountId, sharesToMove.toString());
    }

    updatePositionRowCurrentAvailableShares() {
        let newMasterCurrentAvailableShares = Object.entries(this.MasterPositionRow)
        .filter(([key, value]) => key.startsWith("Shares to move") && typeof value === "number")
        .reduce((availableMasterPositionSharesToMove, [key, value]: [string, number | any] ) => {
            return availableMasterPositionSharesToMove - (value as number) 
        }, this.AvailableShares)
        this.params.data.masterNode.node?.setDataValue('currentAvailableShares', newMasterCurrentAvailableShares)
    }

    // Roll lot edits back up to the master position row so the parent totals stay accurate while manual lot editing is in progress.
    handlePositionRowTableCalculations() {
        this.updatePositionRowSharesAndEmv();
        this.updatePositionRowCurrentAvailableShares();
    }

    get AvailableShares(): number {
        let availableShares = this.MasterPositionRow['Available shares']?.split(' ')[0];
        availableShares = availableShares.replace(',', '');
        return Number(availableShares);
    }

    get MasterPositionRow() {
        return this.params.data.masterNode.data;
    }

    get HasErrorMasterPositionRow() {
        return this.MasterPositionRow['hasError ' + this.AccountId] > 0;
    }

    // Rebuild the master row from the store snapshot so only valid lots contribute to shares and market value.
    updatePositionRowSharesAndEmv() {
        let newMasterShares: number = 0;
        this.store.select(selectMultipleToAccountsPositionLotTableData).pipe(take(1)).subscribe(rowDataById => {
            let storeMasterPositionRow = rowDataById[this.AccountId].positionData.find(positionMasterRow => positionMasterRow.schwabSecurityId === this.MasterPositionRow.schwabSecurityId && positionMasterRow.cmtSequenceId === this.MasterPositionRow.cmtSequenceId);
            let unroundedMasterShares = storeMasterPositionRow?.lotDetail.reduce((sharesToMove: number, lot: any): number => {
                if (!lot.lotHasError && lot.lotsToMove && lot.lotsToMove !== "") {
                    return sharesToMove + Number(lot.lotsToMove);
                }
                return sharesToMove;
            }, 0)
            newMasterShares = Number(unroundedMasterShares.toFixed(6)); //round calculation to 6 decimals
        });
        this.updateMasterSharesToMove(newMasterShares === 0 ? '' : newMasterShares, this.AccountId);
        if (!this.HasErrorMasterPositionRow && newMasterShares !== 0) {
            let masterEmv = this.calculateEmv(newMasterShares);
            this.params.data.masterNode.node?.setDataValue('Estimated market value ' + this.AccountId, this.currencyPipe.transform(masterEmv));
        }
        else {
            this.params.data.masterNode.node?.setDataValue('Estimated market value ' + this.AccountId, '');
        }
    }

    // Keep the lot row display columns in sync with the current input so AG Grid can redraw without waiting for the next store emission.
    handleTableCalculations() {
        const newVal = !this.enteredLotValue ? 0 : Number(this.enteredLotValue);
        if (!this.hasError(newVal)) {
            const emv = this.calculateEmv(newVal);
            this.updateEmv(emv === 0 ? '' : emv)
            const totalCostBasis = this.calculateTotalCostBasis(newVal, this.LotsCostBasis, this.TotalAvailableSharesLotLevel);
            this.updateTotalCostBasis(totalCostBasis === 0 ? '' : totalCostBasis, this.AccountId);
            this.params.node.setDataValue('lotHasError ' + this.AccountId, 0);
        } else {
            this.updateEmv('');
            this.updateTotalCostBasis('', this.AccountId);
        }
    }

    onFocus(event: any) {
        if (this.params?.mode === 'render') {
            setTimeout(() => {
                const rowIndex = this.params.node.rowIndex;
                const colKey = this.params.column.getColId();
                const isCurrentCellEditing = this.params.api.getEditingCells().some((cell: any) =>
                    cell.rowIndex === rowIndex && cell.column?.getColId?.() === colKey
                );
                if (isCurrentCellEditing) {
                    return;
                }

                this.params.api.startEditingCell({
                    rowIndex,
                    colKey
                });
            }, 0);
        }
    }

    validateInput(event: any) {
        const inputElem = event.currentTarget as HTMLInputElement;
        if (event.currentTarget as HTMLInputElement) {
            inputElem.focus();
            var existingInput = this.textInput?.nativeElement?.value ?? "";
            if (!/^\d*\.?\d*$/.test(existingInput + event.key)) {
                return false;
            }
        }
        return true;
    }

    getErrorStatus(key: string, name: string, storeName: string) {
        if (name === storeName) {
            return this.params.data['lotHasError ' + key] > 0;
        }
        return false
    }

    updateStorePositions() {
        const lotsToMove = this.enteredLotValue;
        const columnFieldSplit = this.params.column.getColId().split(' ');
        const toAccountId = columnFieldSplit[columnFieldSplit.length - 1];
        const lotId = this.params?.data['lotId'] ?? '';
        const byId: RowDataById = {};
        this.store.select(selectMultipleToAccountsPositionLotTableData).pipe(take(1)).subscribe(rowDataById => {
            Object.entries(rowDataById).forEach(([key, rowData]) => {
                if (key === toAccountId) {
                    let positionData = rowData?.positionData?.map(position => {
                        const lotDetails = position['lotDetail'];
                        let totalCostBasis = position['Symbol'] == this.params.data['Symbol'] ? this.MasterPositionRow[`Total cost basis ${toAccountId}`] : position['Total cost basis'] !== undefined ? position['Total cost basis'] : ''
                        let hasError = this.getErrorStatus(key,this.params.data['Symbol'],position['Symbol'])
                        return {
                            ...position,
                            'Total cost basis': totalCostBasis !== null ? totalCostBasis : '',
                            'lotDetail': this.costBasisMethod === 'None' ? this.updateStoreLotsToMove(lotDetails, lotId, lotsToMove, toAccountId) : this.clearStoreLotsToMove(lotDetails),
                            'hasError': hasError
                        }
                    });
                    let data = {
                        positionData: positionData,
                        sharesToMoveTotal: rowData?.sharesToMoveTotal,
                        estimatedMarketValueTotal: rowData?.estimatedMarketValueTotal,
                        nodeCostBasis: this.costBasisMethod ?? ''
                    };
                    byId[key] = data;
                } else {
                    byId[key] = rowData;
                }
            })
        })
        this.store.dispatch(InternalTransfersActions.itUpdateRowData(byId));
    }

    updateStoreLotsToMove(lots: LotDetails[], lotId: string, lotsToMove: string, toAccountId: string) {
        let lotData = lots.map(lot => {
            if (lotId === lot['lotId']) {
                return {
                    ...lot,
                    'lotsToMove': lotsToMove ?? '',
                    'estimatedMarketValue': this.params.data[`Estimated market value ${toAccountId}`] ?? '',
                    'totalCostBasis': this.params.data[`TotalCostBasis ${toAccountId}`] ?? null,
                    'lotHasError': this.params.data[`lotHasError ${toAccountId}`] ?? null
                }
            } else {
                return { ... lot };
            }
        });
        return lotData;
    }

    clearStoreLotsToMove(lots: LotDetails[]) {
        let lotData = lots.map(lot => {
            return {
                ...lot,
                'lotsToMove': ''
            }
        });
        return lotData;
    }
}